import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import React, { Suspense } from 'react';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  FE_ACTION_LIST_TOOL_KEY,
} from '@cotera/client/v0/actions/framework';
import {
  ModalPageView,
  useModalManager,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { Center, Loading } from '@cotera/client/app/components';
import { useAvailableTools } from '@cotera/client/app/hooks/use-available-tools';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections';
import { ProviderDetailPage } from '@cotera/client/v0/components/tool-connections/provider-detail';
import { getProviderFromToolKey } from '@cotera/client/v0/components/tool-connections/utils';
import { coco } from '@cotera/api';
import { ToolKey } from '@cotera/sdk/core';
import { cn } from '@cotera/client/v0/lib/utils';
import { ScrollArea } from '@cotera/client/v0/components/ui/scroll-area';

const { isCocoToolKey } = coco;

/**
 * Excludes tools that shouldn't surface in the loaded-tools UI: Cotera-included
 * (coco/) tools and the internal frontend-actions list tool.
 */
export const filterDisplayableLoadedTools = (keys: string[]): string[] =>
  keys.filter((k) => !isCocoToolKey(k) && k !== FE_ACTION_LIST_TOOL_KEY);

type Params = {
  loadedToolKeys: string[];
};

type ModalManager = {
  push: (props: {
    view: () => React.ReactNode;
    onClose?: () => void;
    size?: 'auto' | 'lg' | { custom: string };
  }) => void;
};

export class ViewLoadedToolsAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'View Loaded Tools';
  description = 'View tools loaded in this conversation';
  inputSchema = z.object({
    loadedToolKeys: z.array(z.string()),
  });
  group = 'Chat';
  icon = 'wrench';

  constructor(private readonly modalManager: ModalManager) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    payload: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const displayableKeys = filterDisplayableLoadedTools(
      payload.loadedToolKeys
    );
    this.modalManager.push({
      size: {
        // `w-[92vw]` with a cap, matching every other modal in the app. The
        // `min-w-[400px]` this used to carry is wider than a phone, so the
        // dialog hung off the screen with its right column unreachable —
        // `min-width` wins over the shell's own `max-w-[90vw]`.
        custom: 'w-[92vw] max-w-[600px] max-h-[85dvh] overflow-auto',
      },
      view: () => (
        <ModalPageView
          className="px-5 py-4 flex flex-col min-h-0"
          title="Loaded Tools"
          subtitle={`${displayableKeys.length} tool${
            displayableKeys.length !== 1 ? 's' : ''
          } loaded in this conversation`}
        >
          <Suspense
            fallback={
              <Center>
                <Loading.Dots />
              </Center>
            }
          >
            <LoadedToolsModalContent loadedToolKeys={displayableKeys} />
          </Suspense>
        </ModalPageView>
      ),
    });
    return ok({});
  }
}

const LoadedToolCard: React.FC<{
  toolKey: string;
  displayName?: string;
  providerDisplayName?: string;
  onClick: () => void;
}> = ({ toolKey, displayName, providerDisplayName, onClick }) => {
  const tk = ToolKey.fromString(toolKey);
  const Icon =
    PROVIDER_ICONS[tk.provider as keyof typeof PROVIDER_ICONS] ?? (() => null);
  const actionName = displayName ?? tk.displayName();
  const providerLabel = providerDisplayName ?? tk.provider;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-4 py-2.5 border border-border/60 rounded-lg bg-card text-left',
        'hover:border-primary/30 hover:bg-accent/30 hover:shadow-sm transition-all cursor-pointer'
      )}
    >
      <div className="shrink-0 flex items-center justify-center w-8 h-8 rounded-md bg-muted/30">
        <Icon />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="text-xs text-muted-foreground truncate">
          {providerLabel}
        </div>
        <span className="font-medium text-sm truncate">{actionName}</span>
      </div>
    </button>
  );
};

const LoadedToolsModalContent: React.FC<{ loadedToolKeys: string[] }> = ({
  loadedToolKeys,
}) => {
  const modalManager = useModalManager((s) => s.actions);
  const { tools, providers } = useAvailableTools();
  const toolByKey = React.useMemo(() => {
    const map = new Map<string, (typeof tools)[number]>();
    for (const t of tools) {
      map.set(t.key, t);
    }
    return map;
  }, [tools]);

  const providerByKey = React.useMemo(() => {
    const map = new Map<string, (typeof providers)[number]>();
    for (const p of providers) {
      map.set(p.provider, p);
    }
    return map;
  }, [providers]);

  const sortedKeys = React.useMemo(
    () => [...loadedToolKeys].sort((a, b) => a.localeCompare(b)),
    [loadedToolKeys]
  );

  const handleToolClick = React.useCallback(
    (toolKey: string) => {
      const providerKey = getProviderFromToolKey(toolKey);
      const provider = providerByKey.get(providerKey);
      if (provider === undefined) {
        return;
      }
      modalManager.push({
        size: 'lg',
        view: () => (
          <ProviderDetailPage provider={provider} expandedToolKey={toolKey} />
        ),
      });
    },
    [modalManager, providerByKey]
  );

  return (
    <ScrollArea className="flex-1 min-h-0 -mx-2 px-2">
      <div className="flex flex-col gap-1.5 pb-4">
        {sortedKeys.map((key) => {
          const tool = toolByKey.get(key);
          return (
            <LoadedToolCard
              key={key}
              toolKey={key}
              displayName={tool?.name}
              providerDisplayName={tool?.providerDisplayName}
              onClick={() => handleToolClick(key)}
            />
          );
        })}
      </div>
    </ScrollArea>
  );
};
